/**
 * Options step 1, Delivery: the shortlink TLD (Auto = SMSWiser picks the
 * domain; the catalog carries that option), the shortlink format — where the
 * click id goes and whether links start with https, the same control the
 * drops pages use — and batching.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { HintDot, SearchableSelect } from '@/components/ui';
import { ShortlinkOptions } from '@/features/smswiser-campaigns/components/drop-modals';
import { useCountry } from '@/hooks/smswiser/core/useSMSWiser';

import { FIELD_LABEL } from '../general/fields/field-chrome';
import type { ContainerEditor } from '../state/useContainerEditor';

interface Props {
  editor: ContainerEditor;
  countryKey: string;
}

const AUTO = 'auto';
const isAuto = (v: string | null | undefined) => ['', AUTO, '_auto'].includes((v ?? '').trim().toLowerCase());

const NumberField: FC<{ value: number | null; label: string; suffix: string; onValue: (v: number | null) => void }> = ({ value, label, suffix, onValue }) => (
  <label className="flex items-center gap-1 text-xs text-muted-foreground">
    <input
      type="number"
      min={1}
      value={value ?? ''}
      placeholder="—"
      aria-label={label}
      onChange={(e) => onValue(e.target.value === '' ? null : Number(e.target.value))}
      className={`h-7 w-20 rounded-md border border-input bg-transparent px-2 font-mono text-xs outline-none ${value === null ? 'opacity-60' : ''}`}
    />
    {suffix}
  </label>
);

export const DeliveryDefaults: FC<Props> = ({ editor, countryKey }) => {
  const country = useCountry(countryKey);
  const { draft, setSendDefaults } = editor;
  const d = draft.sendDefaults;
  const tlds = country.data?.shortLinkTlds ?? [];
  const tldOptions = [
    ...(tlds.some((t) => isAuto(t.value)) ? [] : [{ value: AUTO, label: 'Auto' }]),
    ...tlds.map((t) => ({ value: isAuto(t.value) ? AUTO : t.value, label: t.displayName || t.value })),
  ];
  return (
    <div className="flex flex-col gap-3">
      <div className="flex w-[17rem] max-w-full flex-col gap-1.5">
        <span className={`${FIELD_LABEL} flex items-center gap-1`}>
          Shortlink TLD
          <HintDot label="Auto lets SMSWiser pick the domain for each drop." />
        </span>
        <SearchableSelect
          options={tldOptions}
          value={isAuto(d.shortLinkTldValue) ? AUTO : (d.shortLinkTldValue ?? AUTO)}
          onChange={(value) => setSendDefaults({ shortLinkTldValue: isAuto(String(value)) ? null : String(value) })}
          placeholder="Auto"
        />
      </div>
      <div className="w-[17rem] max-w-full">
        <ShortlinkOptions
          compact
          clickIdStructure={d.clickIdStructure}
          setClickIdStructure={(v) => v !== 'unchanged' && v !== 'mixed' && setSendDefaults({ clickIdStructure: v })}
          domainScheme={d.domainScheme}
          setDomainScheme={(v) => v !== 'unchanged' && v !== 'mixed' && setSendDefaults({ domainScheme: v })}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <span className={`${FIELD_LABEL} flex items-center gap-1`}>
          Batching
          <HintDot label="Amount per batch × minutes between batches. Empty = everything sends at once. Mutually exclusive with AI optimization." />
        </span>
        <div className="flex items-center gap-2">
          <NumberField value={d.batchAmount} label="Batch amount" suffix="msgs ×" onValue={(v) => setSendDefaults({ batchAmount: v })} />
          <NumberField value={d.batchDelay} label="Batch delay" suffix="min" onValue={(v) => setSendDefaults({ batchDelay: v })} />
        </div>
      </div>
    </div>
  );
};
